import React, { useState } from 'react';
import styled from 'styled-components';
import { layout } from 'src/components/base';
import {
  ButtonIcon,
  PrimaryButton,
  TextButton,
} from '@prisjakt/ui/button/variants';
import { IconPlus } from '@prisjakt/ui/icon';
import { Column } from '@prisjakt/ui/list/variants';
import { TitleMediumText, TitleSmallText } from '@prisjakt/ui/text/variants';
import { useAppContext } from 'src/components/app-context';
import { useListContext } from '../../list-context';
import { RadioButton } from '@prisjakt/ui/radiobutton';
import { List } from '../../types';
import { Modal } from '../../../../styles';

type Props = {
  addToList: (listId?: number) => void;
  addToMyProducts: () => void;
  closeModal: () => void;
  isOpen: boolean;
  lists: List[];
};

export const ListModal = (props: Props) => {
  const { addToList, addToMyProducts, closeModal, isOpen, lists } = props;

  const { t } = useAppContext();
  const { fetchMoreLists, moreLists, pageInfo } = useListContext();

  const [selectedValue, setSelectedValue] = useState<number | string>();

  const handleOptionClick = (listId: number | string) => {
    setSelectedValue(listId);
  };
  const onSubmit = () => {
    if (selectedValue === 'my-products') {
      addToMyProducts();
    } else if (typeof selectedValue === 'number') {
      addToList(selectedValue);
    }
    closeModal();
  };
  const handleSaveToNewListClick = () => {
    addToList();
    closeModal();
  };

  return (
    <StyledModal
      isOpen={isOpen}
      onRequestClose={closeModal}
      shouldShowCloseIcon
      width={526}
    >
      <Column gap={1.5}>
        <TitleMediumText>{t('lists', 'list.save-to-list')}</TitleMediumText>

        <ListWrapper>
          <li>
            <RadioButton
              id="my-products"
              name="list"
              selectedValue={selectedValue}
              value="my-products"
              text={t('lists', 'my-products.title')}
              onClickHandler={() => handleOptionClick('my-products')}
            />
          </li>

          {[...lists, ...(moreLists || [])].map(list => (
            <li key={list.id}>
              <RadioButton
                id={list.id.toString()}
                name="list"
                selectedValue={selectedValue}
                value={list.id}
                text={list.name}
                onClickHandler={() => handleOptionClick(list.id)}
              />
            </li>
          ))}

          {pageInfo?.hasNext && (
            <TextButton onClick={fetchMoreLists}>
              {t('common', 'button.show-more')}
            </TextButton>
          )}
        </ListWrapper>

        <ButtonsWrapper>
          <SaveToNewListButton onClick={handleSaveToNewListClick}>
            <ButtonIcon>
              <IconPlus />
            </ButtonIcon>
            <TitleSmallText>
              {t('lists', 'list.save-to-new-list')}
            </TitleSmallText>
          </SaveToNewListButton>
          <SubmitButton onClick={onSubmit}>
            {t('common', 'button.save')}
          </SubmitButton>
        </ButtonsWrapper>
      </Column>
    </StyledModal>
  );
};

const ListWrapper = styled.ul`
  display: grid;
  gap: ${layout.measurements.spacing025};
  margin: 0;
  list-style: none;
  overflow: hidden;
`;

const ButtonsWrapper = styled.div`
  display: grid;
  grid-template-rows: 1fr 1fr;
  grid-row-gap: ${layout.measurements.spacing100};
`;

const SaveToNewListButton = styled(TextButton)`
  padding: ${layout.measurements.spacing025};

  &:active,
  &:hover,
  &:focus {
    background: none;
    color: ${props => props.theme.text.primary};
  }
`;

const SubmitButton = styled(PrimaryButton)`
  width: 100%;
`;

const StyledModal = styled(Modal)`
  && {
    max-height: 90vh;
    overflow-y: scroll;
  }
`;
